import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  User,
  Mail,
  Phone,
  Calendar,
  ShieldCheck,
  HardHat,
  Briefcase,
  UserCog,
  CheckCircle,
  XCircle,
  Clock,
  ArrowLeft,
  Building,
  Users,
  FileText,
  Activity,
  Settings,
} from "lucide-react";
import { formatDistanceToNow, format, subDays, isAfter } from "date-fns";
import { User as UserType } from "../../../types/user";
import PageBreadcrumb from "../../../shared/PageBreadCrumb";
import api from "../../../services/axiosInstance";

// Helper function to safely format dates
const safeFormatDate = (dateValue: string | Date | null | undefined, formatString: string): string => {
  if (!dateValue) return "N/A";
  
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    return format(date, formatString);
  } catch (error) {
    return "Invalid Date";
  }
};

// Helper function to safely format relative time
const safeFormatRelativeTime = (dateValue: string | Date | null | undefined): string => {
  if (!dateValue) return "Never";
  
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    return "Invalid Date";
  }
};

// Helper function to check if date is recent
const isRecentDate = (dateValue: string | Date | null | undefined): boolean => {
  if (!dateValue) return false;
  
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return false;
    }
    return isAfter(date, subDays(new Date(), 7));
  } catch (error) {
    return false;
  }
};

const roleStyles: Record<string, { 
  icon: React.ReactElement; 
  className: string; 
  bgColor: string;
}> = {
  admin: { 
    icon: <ShieldCheck size={16} />, 
    className: "text-blue-700 bg-blue-50 border-blue-200",
    bgColor: "bg-blue-600"
  },
  site_supervisor: {
    icon: <UserCog size={16} />,
    className: "text-orange-700 bg-orange-50 border-orange-200",
    bgColor: "bg-orange-600"
  },
  site_engineer: {
    icon: <HardHat size={16} />,
    className: "text-teal-700 bg-teal-50 border-teal-200",
    bgColor: "bg-teal-600"
  },
  manager: { 
    icon: <Briefcase size={16} />, 
    className: "text-slate-700 bg-slate-50 border-slate-200",
    bgColor: "bg-slate-600"
  },
  head_supervisor: { 
    icon: <ShieldCheck size={16} />, 
    className: "text-purple-700 bg-purple-50 border-purple-200",
    bgColor: "bg-purple-600"
  },
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case "active":
      return <CheckCircle size={16} className="text-green-600" />;
    case "inactive":
      return <XCircle size={16} className="text-red-600" />;
    default:
      return <Clock size={16} className="text-gray-500" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "active":
      return "text-green-700 bg-green-50 border-green-200";
    case "inactive":
      return "text-red-700 bg-red-50 border-red-200";
    default:
      return "text-gray-700 bg-gray-50 border-gray-200";
  }
};

export default function UserDetails() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) {
        setError("User ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(`/users/${userId}`);
        setUser(response.data);
        setError(null);
      } catch (err: any) {
        console.error("Failed to fetch user details", err);
        setError(err.response?.data?.message || "Failed to fetch user details");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [userId]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <PageBreadcrumb pageTitle="User Details" />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <PageBreadcrumb pageTitle="User Details" />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <XCircle size={48} className="text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading User</h3>
            <p className="text-gray-600 mb-4">{error || "User not found"}</p>
            <button
              onClick={() => navigate("/admin/users")}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowLeft size={16} />
              Back to Users
            </button>
          </div>
        </div>
      </div>
    );
  }

  const roleName: string =
    typeof user.role === "string"
      ? user.role
      : user.role && typeof user.role === "object" && typeof user.role.name === "string"
      ? user.role.name
      : "unknown";
  const roleKey: string = roleName.toLowerCase().replace(/ /g, "_");
  const roleStyle = roleStyles[roleKey] || roleStyles.admin;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <PageBreadcrumb pageTitle="User Details" />
      
      {/* Header Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate("/admin/users")}
              className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">User Profile</h1>
              <p className="text-sm text-gray-600">View user information and account details</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {/* Avatar Section */}
            <div className="relative">
              <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 font-medium text-lg border border-gray-200">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                <CheckCircle size={12} className="text-white" />
              </div>
            </div>

            {/* User Info */}
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">{user.name}</h2>
              <p className="text-gray-600 mb-3">{user.email}</p>
              
              {/* Role Badge */}
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-md text-sm font-medium border ${roleStyle.className}`}>
                {roleStyle.icon}
                <span>{roleName.replace(/_/g, " ")}</span>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 sm:gap-6">
              <div className="text-center">
                <div className="text-sm font-medium text-gray-900">{user.status === "inactive" ? "Inactive" : "Active"}</div>
                <div className="text-xs text-gray-500">Status</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-900">
                  {safeFormatDate(user.createdAt, "MMM yyyy")}
                </div>
                <div className="text-xs text-gray-500">Joined</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-900">
                  {user.lastLoginAt ? (
                    isRecentDate(user.lastLoginAt) ? (
                      safeFormatRelativeTime(user.lastLoginAt)
                    ) : (
                      safeFormatDate(user.lastLoginAt, "MMM dd")
                    )
                  ) : (
                    "Never"
                  )}
                </div>
                <div className="text-xs text-gray-500">Last Login</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Personal Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information Card */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="border-b border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <User size={18} className="text-gray-600" />
                <h3 className="text-base font-medium text-gray-900">Personal Information</h3>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Full Name</label>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                      <User size={16} className="text-gray-500" />
                      <span className="text-gray-900">{user.name}</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Email Address</label>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                      <Mail size={16} className="text-gray-500" />
                      <div className="flex-1">
                        <span className="text-gray-900">{user.email}</span>
                        {user.isEmailVerified && (
                          <div className="flex items-center gap-1 mt-1">
                            <CheckCircle size={12} className="text-green-500" />
                            <span className="text-green-600 text-xs">Verified</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {user.mobile && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Phone Number</label>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <Phone size={16} className="text-gray-500" />
                        <span className="text-gray-900">+91 {user.mobile}</span>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Account Status</label>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                      {getStatusIcon(user.status)}
                      <span className={`text-xs font-medium px-2 py-1 rounded-md border ${getStatusColor(user.status)}`}>
                        {user.status === "inactive" ? "Inactive" : "Active"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Account Timeline Card */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="border-b border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-gray-600" />
                <h3 className="text-base font-medium text-gray-900">Account Timeline</h3>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                      <Calendar size={14} className="text-blue-600" />
                    </div>
                    <h4 className="text-sm font-medium text-gray-900">Account Created</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">
                    {safeFormatDate(user.createdAt, "dd MMM yyyy, hh:mm a")}
                  </p>
                  <p className="text-xs text-gray-500">User account was created</p>
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                      <Calendar size={14} className="text-purple-600" />
                    </div>
                    <h4 className="text-sm font-medium text-gray-900">Last Updated</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">
                    {safeFormatDate(user.updatedAt, "dd MMM yyyy, hh:mm a")}
                  </p>
                  <p className="text-xs text-gray-500">Profile was last modified</p>
                </div>
              </div>
            </div>
          </div>

          {/* Project Assignments - Placeholder */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="border-b border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <Building size={18} className="text-gray-600" />
                <h3 className="text-base font-medium text-gray-900">Project Assignments</h3>
              </div>
            </div>
            
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Building size={24} className="text-gray-400" />
              </div>
              <h4 className="text-base font-medium text-gray-900 mb-2">Project Management</h4>
              <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
                Project assignment information will be available once the project management module is implemented.
              </p>
              <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto">
                <div className="bg-blue-50 rounded-md p-3 border border-blue-100">
                  <Activity size={16} className="text-blue-600 mx-auto mb-1" />
                  <p className="text-blue-700 font-medium text-xs">Active Projects</p>
                </div>
                <div className="bg-green-50 rounded-md p-3 border border-green-100">
                  <FileText size={16} className="text-green-600 mx-auto mb-1" />
                  <p className="text-green-700 font-medium text-xs">Completed</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Role & Actions */}
        <div className="space-y-6">
          {/* Role Details Card */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="border-b border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} className="text-gray-600" />
                <h3 className="text-base font-medium text-gray-900">Role Details</h3>
              </div>
            </div>
            
            <div className="p-6">
              <div className={`flex items-center gap-3 p-4 rounded-md border ${roleStyle.className} mb-4`}>
                {roleStyle.icon}
                <div>
                  <span className="font-medium text-sm">
                    {roleName.replace(/_/g, " ")}
                  </span>
                  <p className="text-xs opacity-75">User Role</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-md">
                  <ShieldCheck size={14} className="text-blue-600" />
                  <span className="text-gray-700 text-sm">System Access</span>
                </div>
                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-md">
                  <Settings size={14} className="text-green-600" />
                  <span className="text-gray-700 text-sm">Management Tools</span>
                </div>
                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-md">
                  <Users size={14} className="text-purple-600" />
                  <span className="text-gray-700 text-sm">User Operations</span>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Stats Card */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="border-b border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <Activity size={18} className="text-gray-600" />
                <h3 className="text-base font-medium text-gray-900">Account Status</h3>
              </div>
            </div>
            
            <div className="p-6 space-y-3">
              <div className="bg-green-50 rounded-md p-3 border border-green-100">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle size={14} className="text-green-600" />
                  <span className="text-green-700 font-medium text-sm">Login Status</span>
                </div>
                <p className="text-green-600 text-xs">
                  {user.lastLoginAt ? "Regular user" : "New user"}
                </p>
              </div>

              <div className="bg-blue-50 rounded-md p-3 border border-blue-100">
                <div className="flex items-center gap-2 mb-1">
                  <User size={14} className="text-blue-600" />
                  <span className="text-blue-700 font-medium text-sm">Account</span>
                </div>
                <p className="text-blue-600 text-xs">
                  {user.status === "active" ? "Active & Verified" : "Inactive"}
                </p>
              </div>

              <div className="bg-purple-50 rounded-md p-3 border border-purple-100">
                <div className="flex items-center gap-2 mb-1">
                  <Mail size={14} className="text-purple-600" />
                  <span className="text-purple-700 font-medium text-sm">Email</span>
                </div>
                <p className="text-purple-600 text-xs">
                  {user.isEmailVerified ? "Verified" : "Not verified"}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Actions Card */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="border-b border-gray-200 p-4">
              <h3 className="text-base font-medium text-gray-900">Quick Actions</h3>
            </div>
            
            <div className="p-4 space-y-2">
              <button className="w-full flex items-center gap-2 p-3 text-gray-700 hover:bg-gray-50 rounded-md border border-gray-200 transition-colors">
                <Mail size={16} className="text-gray-500" />
                <span className="text-sm">Send Message</span>
              </button>
              
              <button className="w-full flex items-center gap-2 p-3 text-gray-700 hover:bg-gray-50 rounded-md border border-gray-200 transition-colors">
                <Users size={16} className="text-gray-500" />
                <span className="text-sm">View Team</span>
              </button>
              
              <button className="w-full flex items-center gap-2 p-3 text-gray-700 hover:bg-gray-50 rounded-md border border-gray-200 transition-colors">
                <FileText size={16} className="text-gray-500" />
                <span className="text-sm">View Reports</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 